import jsep from 'jsep';
import { getUserError } from './miscUtils';

const parsedExpressionCache: { [expressionString: string]: { expression: jsep.Expression; error: string | null } } = {};

export type TpExpressionContext = {
  expressionString: string;
  parameters: { [name: string]: any };
  functions?: { [name: string]: (argumentList: any[]) => any };
};

// Safely evaluates a simple expression, provided parameter data
// For security reasons, this does not rely on eval, but uses a lightweight expression parser & evaluator
// Use cases: expressions defined in yaml templates (e.g. form data validation, conditional visibility of elements in a form...)
export function evalExpression(ctx: TpExpressionContext): any {
  const { expressionString, parameters } = ctx;
  if (!parsedExpressionCache[expressionString]) {
    try {
      parsedExpressionCache[expressionString] = {
        expression: jsep(expressionString),
        error: null,
      };
    } catch (error) {
      parsedExpressionCache[expressionString] = {
        expression: jsep('null'),
        error: `Error parsing expression "${expressionString}": ${String(error)}`,
      };
    }
  }

  if (parsedExpressionCache[expressionString].error) throw getUserError(parsedExpressionCache[expressionString].error);

  function recursiveEval(expr: jsep.Expression): any {
    if (expr.type == 'BinaryExpression') {
      const binExpr = expr as jsep.BinaryExpression;
      const left = recursiveEval(binExpr.left);
      const right = recursiveEval(binExpr.right);
      if (binExpr.operator == '||') return left || right;
      if (binExpr.operator == '&&') return left && right;
      if (binExpr.operator == '==') return left == right;
      if (binExpr.operator == '+') return left + right;
      if (binExpr.operator == '-') return left - right;
      if (binExpr.operator == '*') return left * right;
      if (binExpr.operator == '/') return left / right;
      if (binExpr.operator == '<') return left < right;
      if (binExpr.operator == '<=') return left <= right;
      if (binExpr.operator == '>') return left > right;
      if (binExpr.operator == '>=') return left >= right;
      throw getUserError(`Unsupported binary operator ${binExpr.operator} in "${expressionString}"`);
    }
    if (expr.type == 'UnaryExpression') {
      const unExpr = expr as jsep.UnaryExpression;
      const argument = recursiveEval(unExpr.argument);
      if (unExpr.operator == '!') return !argument;
      throw getUserError(`Unsupported unary operator ${unExpr.operator} in "${expressionString}"`);
    }
    if (expr.type == 'Identifier') {
      const idExpr = expr as jsep.Identifier;
      if (!Object.keys(parameters).includes(idExpr.name))
        throw getUserError(`Invalid parameter "${idExpr.name}" in "${expressionString}"`);
      return parameters[idExpr.name];
    }
    if (expr.type == 'Literal') {
      const litExpr = expr as jsep.Literal;
      return litExpr.value;
    }
    if (expr.type == 'ConditionalExpression') {
      const condExpr = expr as jsep.ConditionalExpression;
      if (recursiveEval(condExpr.test)) return recursiveEval(condExpr.consequent);
      else return recursiveEval(condExpr.alternate);
    }
    if (expr.type == 'CallExpression') {
      const callExpr = expr as jsep.CallExpression;
      const functionName = String(callExpr.callee.name);
      if (!ctx.functions || !ctx.functions![functionName])
        throw getUserError(`Invalid function "${functionName}" in "${expressionString}"`);
      const argumentList = callExpr.arguments.map((arg) => recursiveEval(arg));
      try {
        return ctx.functions![functionName](argumentList);
      } catch (e) {
        throw getUserError(`Error while evaluating "${functionName}" in "${expressionString}: ${e}"`);
      }
    }
    throw getUserError(`Unsupported type ${expr.type} in "${expressionString}"`);
  }

  return recursiveEval(parsedExpressionCache[expressionString].expression);
}

export function evalBooleanExpression(ctx: TpExpressionContext): boolean {
  const rs = evalExpression(ctx);
  if (typeof rs != 'boolean') throw getUserError(`Expression should return a boolean: ${ctx.expressionString}`);
  return rs as boolean;
}
